import { useEffect, useRef, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { WishlistButton, Notification, Portal } from '@packages/shared';
import type { WishlistButtonProps, NotificationProps } from '@packages/shared';
import type { AvailabilityState } from '@packages/gql/generated/shopping/graphql';
import {
  type GlycerinAddToWishListPayload,
  type GTMEventGlycerinAddToWishlist,
  type GlycerinRemoveFromWishListPayload,
  type GTMEventGlycerinRemoveFromWishList,
  type GlycerinWishListProduct,
  useTracking,
} from '@packages/tracking';
import { useWishlist } from './useWishlist';

export type WishlistTrackingProps = Omit<GlycerinAddToWishListPayload, 'addedProducts'> &
  Omit<GlycerinRemoveFromWishListPayload, 'removedProducts'> & {
    products: GlycerinWishListProduct[];
  };

export type WishlistButtonWithApiProps = {
  /** The sku of the desired product */
  sku: string;
  /** Availability-state as string. Can be undefined if state is unknown. Can also be state 'UNSELECTED' which is identical to undefined. */
  availability?: AvailabilityState;
  /** name of the product to be added */
  productName?: string;
  /** icon only version
   * @default true
   */
  iconOnly?: boolean;
  trackingProps?: WishlistTrackingProps;
} & Omit<WishlistButtonProps, 'isActive'>;

const wishlistMessages = defineMessages({
  removeSuccess: {
    id: 'add-to-wishlist.remove-success',
    defaultMessage:
      '{productName, select, undefined {Der Artikel} other {{productName}}} wurde erfolgreich vom Merkzettel entfernt.',
  },
  removeError: {
    id: 'add-to-wishlist.remove-error',
    defaultMessage:
      '{productName, select, undefined {Der Artikel} other {{productName}}} konnte nicht vom Merkzettel entfernt werden.',
  },
  addSuccess: {
    id: 'add-to-wishlist.add-success',
    defaultMessage:
      '{productName, select, undefined {Der Artikel} other {{productName}}} wurde dem Merkzettel hinzugefügt.',
  },
  addError: {
    id: 'add-to-wishlist.add-error',
    defaultMessage:
      '{productName, select, undefined {Der Artikel} other {{productName}}} konnte nicht auf dem Merkzettel gespeichert werden.',
  },
  removeLabel: {
    id: 'add-to-wishlist.label.remove',
    defaultMessage: 'Vom Merkzettel entfernen',
  },
  addLabel: {
    id: 'add-to-wishlist.label.add',
    defaultMessage: 'Auf den Merkzettel',
  },
});

// TODO(api-layer): needs to be reworked when wishlist resovler are implemented!

/**
 * WishlistButtonWithApi connects to wishlist api, sets button state and displays a Notification on change
 *
 * ATTENTION: changes to this component also have to be made for the `WishlistButtonWithApi` component in `packages/recommendations/src/copiedComponentsFromModulesToAvoidDependencyCycles`
 */
export const WishlistButtonWithApi = ({
  sku,
  productName,
  availability,
  iconOnly = true,
  trackingProps,
  ...rest
}: WishlistButtonWithApiProps) => {
  const { formatMessage } = useIntl();
  const dispatchGTMEvent = useTracking();
  const [notification, setNotification] = useState<
    | {
        message: string;
        severity: NotificationProps['severity'];
      }
    | undefined
  >(undefined);

  const { products, custom, name, variantIds, wishListId } = trackingProps ?? {};

  const portalRef = useRef<HTMLElement>();

  const { triggerAddToWishlist, triggerDeleteFromWishlist, revalidateWishlist, data, isLoading } =
    useWishlist(sku, availability === 'UNSELECTED');

  const isActive = !!data?.wishlist?.includes(sku);

  useEffect(() => {
    portalRef.current = document.getElementById('notification-portal') || undefined;
  }, []);

  const clickHandler = async () => {
    if (isActive) {
      try {
        const result = await triggerDeleteFromWishlist();
        if (result?.success) {
          setNotification({
            severity: 'warning',
            message: formatMessage(wishlistMessages.removeSuccess, { productName }),
          });
          revalidateWishlist();
          dispatchGTMEvent({
            event: 'ga_event',
            eventAction: 'wishlistRemove',
            eventCategory: 'userInteraction',
            eventLabel: sku,
          });
          dispatchGTMEvent<GTMEventGlycerinRemoveFromWishList>({
            event: 'RemoveFromWishList',
            RemoveFromWishListData: {
              removedProducts: products ?? [],
              custom,
              name,
              variantIds,
              wishListId,
            },
          });
        } else {
          throw new Error();
        }
      } catch {
        setNotification({
          severity: 'error',
          message: formatMessage(wishlistMessages.removeError, { productName }),
        });
      }
    } else {
      try {
        const result = await triggerAddToWishlist();
        if (result?.success) {
          setNotification({
            severity: 'success',
            message: formatMessage(wishlistMessages.addSuccess, { productName }),
          });
          revalidateWishlist();
          dispatchGTMEvent({
            event: 'ga_event',
            eventAction: 'wishlistAdd',
            eventCategory: 'userInteraction',
            eventLabel: sku,
          });
          dispatchGTMEvent<GTMEventGlycerinAddToWishlist>({
            event: 'AddToWishList',
            AddToWishListData: {
              addedProducts: products ?? [],
              custom,
              name,
              variantIds,
              wishListId,
            },
          });
        } else {
          throw new Error();
        }
      } catch {
        setNotification({
          severity: 'error',
          message: formatMessage(wishlistMessages.addError, { productName }),
        });
      }
    }
  };

  return (
    <>
      {notification && (
        <Portal container={portalRef.current}>
          <Notification
            open={!!notification}
            severity={notification.severity}
            onClose={() => setNotification(undefined)}
            autoHideDuration={2500}
          >
            {notification.message}
          </Notification>
        </Portal>
      )}
      <WishlistButton
        {...rest}
        isActive={isActive}
        onClick={clickHandler}
        loading={isLoading}
        variant={iconOnly ? rest.variant : 'contained'}
        disableRipple={iconOnly}
      >
        {iconOnly
          ? null
          : formatMessage(isActive ? wishlistMessages.removeLabel : wishlistMessages.addLabel)}
      </WishlistButton>
    </>
  );
};
